import { ref, computed } from 'vue'
import { fetchSttCredentials, ElevenLabsScribeSession } from '@/composables/useElevenLabsScribe'

function floatTo16BitPcmLittleEndian(input: Float32Array): ArrayBuffer {
    const buffer = new ArrayBuffer(input.length * 2)
    const view = new DataView(buffer)
    for (let i = 0; i < input.length; i++) {
        const s = Math.max(-1, Math.min(1, input[i]))
        view.setInt16(i * 2, s < 0 ? s * 0x8000 : s * 0x7fff, true)
    }
    return buffer
}

function resampleFloat32(input: Float32Array, fromRate: number, toRate: number): Float32Array {
    if (fromRate === toRate) {
        return input
    }
    const ratio = fromRate / toRate
    const newLength = Math.floor(input.length / ratio)
    const result = new Float32Array(newLength)
    for (let i = 0; i < newLength; i++) {
        const srcIdx = Math.floor(i * ratio)
        result[i] = input[srcIdx]
    }
    return result
}

function arrayBufferToBase64(buffer: ArrayBuffer): string {
    const bytes = new Uint8Array(buffer)
    let binary = ''
    for (let i = 0; i < bytes.byteLength; i++) {
        binary += String.fromCharCode(bytes[i])
    }
    return btoa(binary)
}

export const useVoiceRecognition = () => {
    const isRecording = ref(false)
    const isProcessing = ref(false)
    const liveTranscription = ref('')
    const mediaStream = ref<MediaStream | null>(null)
    const audioContext = ref<AudioContext | null>(null)
    const processor = ref<ScriptProcessorNode | null>(null)
    const sourceNode = ref<MediaStreamAudioSourceNode | null>(null)
    const scribeSession = ref<ElevenLabsScribeSession | null>(null)
    const statusError = ref('')

    const statusMessage = computed(() => {
        if (statusError.value) {
            return statusError.value
        }
        if (isProcessing.value) {
            return 'Processing your command...'
        }
        if (isRecording.value) {
            return 'Say something like "Add 2 apples" or "How many eggs do I have?"'
        }
        return 'Ready to listen'
    })

    const setProcessing = (processing: boolean) => {
        isProcessing.value = processing
    }

    const startRecording = async () => {
        statusError.value = ''
        try {
            const stream = await navigator.mediaDevices.getUserMedia({ audio: true })
            mediaStream.value = stream

            const creds = await fetchSttCredentials()
            const session = new ElevenLabsScribeSession({
                onPartial: (text) => {
                    liveTranscription.value = text
                },
                onInputError: (msg) => {
                    console.error('Scribe input error:', msg)
                    statusError.value = 'Transcription error. Check microphone or API setup.'
                },
            })
            await session.connect(creds)
            scribeSession.value = session

            const ctx = new AudioContext()
            audioContext.value = ctx
            const src = ctx.createMediaStreamSource(stream)
            sourceNode.value = src

            const bufferSize = 4096
            const proc = ctx.createScriptProcessor(bufferSize, 1, 1)
            processor.value = proc

            const mute = ctx.createGain()
            mute.gain.value = 0

            proc.onaudioprocess = (event) => {
                if (!scribeSession.value) {
                    return
                }
                const input = event.inputBuffer.getChannelData(0)
                const at16k = resampleFloat32(input, ctx.sampleRate, 16000)
                const pcm = floatTo16BitPcmLittleEndian(at16k)
                const b64 = arrayBufferToBase64(pcm)
                scribeSession.value.sendPcmChunk(b64, false)
            }

            src.connect(proc)
            proc.connect(mute)
            mute.connect(ctx.destination)

            liveTranscription.value = ''
            isRecording.value = true
        } catch (error) {
            console.error('Error starting recording:', error)
            statusError.value =
                error instanceof Error ? error.message : 'Unable to start voice capture.'
            alert(statusError.value)
            cleanupRecording()
        }
    }

    const stopRecording = async (): Promise<string> => {
        if (!isRecording.value) {
            return ''
        }

        isRecording.value = false
        isProcessing.value = true

        const snapshotPartial = liveTranscription.value
        const session = scribeSession.value

        if (processor.value) {
            processor.value.disconnect()
            processor.value.onaudioprocess = null
            processor.value = null
        }
        if (sourceNode.value) {
            sourceNode.value.disconnect()
            sourceNode.value = null
        }
        if (audioContext.value) {
            audioContext.value.close().catch(() => {})
            audioContext.value = null
        }
        if (mediaStream.value) {
            mediaStream.value.getTracks().forEach((track) => track.stop())
            mediaStream.value = null
        }

        let transcript = ''
        if (session) {
            transcript = await session.commitEndAwaitSettled()
            transcript = transcript.trim() || snapshotPartial.trim()
            session.close()
        }

        scribeSession.value = null
        liveTranscription.value = ''

        return transcript.trim()
    }

    const cleanupRecording = () => {
        if (processor.value) {
            processor.value.disconnect()
            processor.value.onaudioprocess = null
            processor.value = null
        }
        if (sourceNode.value) {
            sourceNode.value.disconnect()
            sourceNode.value = null
        }
        if (audioContext.value) {
            audioContext.value.close().catch(() => {})
            audioContext.value = null
        }
        if (mediaStream.value) {
            mediaStream.value.getTracks().forEach((track) => track.stop())
            mediaStream.value = null
        }
        scribeSession.value?.close()
        scribeSession.value = null
        isRecording.value = false
    }

    const cleanup = () => {
        cleanupRecording()
        isProcessing.value = false
    }

    return {
        isRecording,
        isProcessing,
        liveTranscription,
        statusMessage,
        startRecording,
        stopRecording,
        setProcessing,
        cleanup,
    }
}
